/**
 * Reversion Triggers — sheet 8 § "Switching Back."
 *
 * Tests the second-hardest skill in exploit play (after identifying
 * the archetype in the first place): updating or reverting the read
 * when evidence shifts. Sheet 8 enumerates 5 triggers:
 *   - Contradicting action (one) — drop magnitude
 *   - State change (drink, buy-in, friend) — drop magnitude
 *   - Time elapsed without confirmation — reduce / revert
 *   - Seat change — reset (might be a different player dynamic now)
 *   - Image change (yours) — temporarily invalidate old reads
 *
 * For grading we collapse to 3 actions:
 *   - **Hold the read** — keep magnitude, the trigger isn't strong enough
 *   - **Drop magnitude** — don't flip but reduce the aggressiveness of the exploit
 *   - **Full revert to GTO** — read is invalid; play baseline until evidence reclarifies
 *
 * Hand-authored vignettes; ~14 questions.
 */

import { pickFrom } from "@/features/quiz/lib/common";

export type ReversionAction = "hold" | "drop_magnitude" | "full_revert";

export const ACTION_LABEL: Record<ReversionAction, string> = {
  hold: "Hold the read",
  drop_magnitude: "Drop magnitude",
  full_revert: "Full revert to GTO",
};

export interface ReversionVignette {
  vignette: string;
  answer: ReversionAction;
  rationale: string;
}

export const REVERSION_TRIGGER_BANK: ReversionVignette[] = [
  {
    vignette: "Your read: STATION (called 3 streets with bottom pair 30 minutes ago). Same villain just check-raised the turn against your c-bet. What now?",
    answer: "drop_magnitude",
    rationale: "Sheet 8: one contradicting action doesn't flip a read, but it should lower magnitude. Don't hero-call light on this hand; treat the raise as more value-skewed than a balanced player's.",
  },
  {
    vignette: "Your read: NIT (folded JJ face-up to a 3-bet earlier). Same villain just opened 5-handed for 4× and 3-bet your raise. What now?",
    answer: "drop_magnitude",
    rationale: "Behavior contradicts the nit baseline. Drop magnitude on the nit exploits (no more wide bluffs) and tighten up against their 3-bet. Don't fully revert yet; one orbit isn't enough.",
  },
  {
    vignette: "Your read: MANIAC (3-bet 5× in 30 minutes, bluffed multiple river barrels). Same villain just folded preflop to a min-raise. What now?",
    answer: "hold",
    rationale: "Even maniacs fold when their hand is unplayable. One preflop fold isn't diagnostic; they still have the maniac frequency profile across many hands. Hold the read.",
  },
  {
    vignette: "You read seat 5 as a STATION an hour ago. Since then villain has been in 4 hands but done nothing diagnostic: no big bets, no showdowns, no clear value calls.",
    answer: "drop_magnitude",
    rationale: "Sheet 8: time elapsed without confirmation. The read is getting stale. Lower magnitude on station exploits until you see fresh confirmation.",
  },
  {
    vignette: "Your read: LIMPER/WHALE. Villain just ordered a drink, lost a buy-in to a suckout, and reloaded with $1000. What now?",
    answer: "drop_magnitude",
    rationale: "State change: emotional plus financial shift. Reload + loss often shifts a whale into tilt-mode (more aggression). Drop magnitude on the limper exploits; expect more raising than baseline.",
  },
  {
    vignette: "You were running well for 2 hours and showed down a big triple-barrel bluff. The reg in seat 3 (your read was SOLID) is now opening wider and 3-betting you light.",
    answer: "full_revert",
    rationale: "Your image changed, so they're adjusting TO YOU. Old read is invalid; play baseline GTO against them until you see their new equilibrium. Don't try to fight back with more bluffs.",
  },
  {
    vignette: "Your read: TILTED MANIAC (just lost a buy-in, has been over-jamming). Villain has now folded 8 hands in a row without putting in money. What now?",
    answer: "drop_magnitude",
    rationale: "Tilt often cools. 8 quiet hands suggests they've settled, but they might re-tilt on another bad beat. Drop magnitude on the tilt exploits (don't hero-call as wide); watch for re-escalation.",
  },
  {
    vignette: "Villain in seat 6 (read: NIT) was just dealt a chair shuffle. Now they're in seat 2. Your read?",
    answer: "hold",
    rationale: "Seat change doesn't change the player. Their archetype is intrinsic to them, not the seat. The DYNAMIC vs your specific position may shift, but the underlying tendency is the same. Hold the read.",
  },
  {
    vignette: "Your read: MANIAC. Same villain just slow-played a set on a wet board, called your turn raise with a flush draw, and turned over JJ on a Q-J-T flop saying 'thought you had AQ.' Two showdowns of disciplined play in a row.",
    answer: "full_revert",
    rationale: "Multiple disciplined showdowns contradict the maniac read directly. They might be a LAG-aware reg, not a true maniac. Revert to baseline GTO until you see clearer evidence again.",
  },
  {
    vignette: "Your read: STATION. Villain just made a polarized river overbet (~150% pot) on a brick runout, then showed a missed flush draw. What now?",
    answer: "full_revert",
    rationale: "Stations don't bluff polar overbet sizes; that's solver-level construction. Whatever they are, they're NOT a station. Revert; observe more before committing to a new archetype.",
  },
  {
    vignette: "Your read: LIMPER/WHALE (limped MP 4 hands in a row, called isos with marginal stuff). They just raised UTG to 5× and snap-called your 3-bet. They show down AA. What now?",
    answer: "hold",
    rationale: "Limp/whales DO trap with AA; it's actually one of their archetypal moves (slow-play monsters preflop). One AA showdown doesn't break the read; it confirms a sub-pattern of the read.",
  },
  {
    vignette: "You arrive at the table; an old note says seat 4 was a TILTED MANIAC three weeks ago. They just sat down for this session. What's your read going in?",
    answer: "full_revert",
    rationale: "Sheet 8: prior reads decay. A 3-week-old note is essentially worthless: players go on vacation, study, change games. Treat them as UNKNOWN → GTO baseline until they do something diagnostic this session.",
  },
  {
    vignette: "Your read: NIT (8% VPIP, folds to all pressure). Villain just c-bet flop, double-barreled turn, AND river jammed for 2× pot on a 4-card flush board. What now?",
    answer: "hold",
    rationale: "This is the SCARIEST line for a nit, and that's exactly why you HOLD the read here. Nits firing 3 streets including a polar river overjam are nutted at the highest frequency of any archetype. Fold all bluff-catchers; the read informs the EXACT fold.",
  },
  {
    vignette: "Your read: SOLID REG. Villain has been making slightly off-baseline plays for the last orbit: opening a bit wider, c-betting more than usual. Nothing extreme, but a noticeable shift.",
    answer: "drop_magnitude",
    rationale: "Subtle drift could be tilt-creep or an adjustment to a perceived dynamic. You're not flipping the read, but stop deferring to the reg label so completely. Reduce GTO-only deference; start considering exploit micro-deviations.",
  },
];

export function generateQuestion(
  rand: () => number = Math.random,
): ReversionVignette {
  return pickFrom(REVERSION_TRIGGER_BANK, rand);
}

export const _internals = { REVERSION_TRIGGER_BANK };
